import { useEffect, useRef, useState } from 'react'

import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Stack,
  Typography,
} from '@mui/material'
import { Chip } from '@workwave-tidal/core/components'

import { preview } from '@/components/form/preview'
import { TerritoryDetail } from './TerritoryDetail'

import { useFetchTerritoriesDriversAndVehicles } from '../../hooks/useFetchTerritoriesDriversAndVehicles'
import { useTerritoriesDetails } from './useTerritoriesDetails'
import { useTexts } from './useTexts'

interface Props {
  territoryNames: Record<string, string>
  courierFilter: uui.domain.client.rm.CourierFilter
}

const DEFAULT_HEIGHT_OF_TWO_ROWS = 56

export function CourierConfig(props: Props) {
  const { courierFilter, territoryNames } = props

  const texts = useTexts()

  const [showAllTerritories, setShowAllTerritories] = useState(false)
  const [isTerritoriesOverflowing, setIsTerritoriesOverflowing] = useState(false)

  const territoriesContainerRef = useRef<HTMLDivElement>(null)

  const driversAndVehiclesData = useFetchTerritoriesDriversAndVehicles()

  const { territoriesCount, territoriesDetails } = useTerritoriesDetails(
    courierFilter,
    territoryNames,
  )

  useEffect(() => {
    // Check if the territory container height exceeds default height of 2 rows
    if (territoriesContainerRef.current) {
      setIsTerritoriesOverflowing(
        territoriesContainerRef.current.scrollHeight > DEFAULT_HEIGHT_OF_TWO_ROWS,
      )
    }
  }, [territoriesCount])

  return (
    <preview.Field
      label={texts.title(territoriesCount)}
      variant="alternative"
      testId="courier-config"
      data-trackid="courier-config"
    >
      <Stack
        gap={0.5}
        direction="row"
        flexWrap="wrap"
        ref={territoriesContainerRef}
        sx={{
          maxHeight: showAllTerritories ? 'none' : `${DEFAULT_HEIGHT_OF_TWO_ROWS}px`,
          overflow: 'hidden',
          transition: 'max-height 0.3s ease',
        }}
      >
        {territoriesDetails.map(territory => (
          <Chip size="small" label={territory.name} key={territory.id} />
        ))}
      </Stack>
      {isTerritoriesOverflowing && (
        <Button
          size="small"
          disableRipple
          sx={{
            textAlign: 'left',
            justifyContent: 'flex-start',
            width: '100%',
            '&:hover': {
              backgroundColor: 'transparent',
              boxShadow: 'none',
            },
          }}
          onClick={() => setShowAllTerritories(!showAllTerritories)}
        >
          <Stack
            gap={1}
            direction="row"
            alignItems="center"
            textAlign="center"
            data-testid={`territories-show-${showAllTerritories ? 'less' : 'more'}`}
            data-trackid={`territories-show-${showAllTerritories ? 'less' : 'more'}`}
          >
            {showAllTerritories ? (
              <>
                {texts.showLess} <KeyboardArrowUp color="primary" fontSize="small" />
              </>
            ) : (
              <>
                {texts.showMore} <KeyboardArrowDown color="primary" fontSize="small" />
              </>
            )}
          </Stack>
        </Button>
      )}

      {driversAndVehiclesData.status === 'ready' && (
        <Accordion>
          <AccordionSummary
            expandIcon={<KeyboardArrowDown color="primary" fontSize="small" />}
            sx={{
              width: 'fit-content',
              '&:hover': {
                backgroundColor: 'transparent',
              },
              '& .MuiAccordionSummary-content': {
                alignItems: 'center',
              },
            }}
          >
            <Typography
              color="primary"
              variant="subtitle2"
              sx={{ fontSize: '13px' }}
              data-testid="show-courier-details"
              data-trackid="show-courier-details"
            >
              {texts.showDetails}
            </Typography>
          </AccordionSummary>

          <AccordionDetails>
            <Stack spacing={1}>
              {territoriesDetails.map(detail => (
                <TerritoryDetail
                  key={detail.id}
                  detail={detail}
                  driversAndVehiclesData={driversAndVehiclesData.data}
                />
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}
    </preview.Field>
  )
}
